import * as R from 'remeda';
import { assertNever } from '../../../../lib/typeUtils';
import type {
  ActivityBlock,
  ActivityRow,
  ActivitySource,
  ActorRef,
} from '../model';
import { isManualSource } from './sourceAttribution';

export const CHANGE_ORIGINS = ['manual', 'automated'] as const;
export type ChangeOrigin = (typeof CHANGE_ORIGINS)[number];

export const HISTORY_EVENTS = [
  'fields',
  'status',
  'queue',
  'automationBlocker',
  'opened',
] as const;
export type HistoryEvent = (typeof HISTORY_EVENTS)[number];

export const FIELD_VISIBILITIES = ['visible', 'hidden'] as const;
export type FieldVisibility = (typeof FIELD_VISIBILITIES)[number];

export type HistoryFilters = {
  changes?: ChangeOrigin[];
  changedBy?: string[];
  event?: HistoryEvent[];
  field?: string[];
  visibility?: FieldVisibility[];
};

export const DEFAULT_HISTORY_FILTERS: HistoryFilters = { changes: ['manual'] };

// Removing a chip sets its key to undefined and "Clear all" resets to {},
// so a defined key is what counts as an applied filter.
export const hasActiveFilters = (filters: HistoryFilters) =>
  Object.values(filters).some(value => value !== undefined);

export type FieldRef = { schemaId: string; label: string };

export const toActorOptions = (blocks: ActivityBlock[]): ActorRef[] =>
  R.pipe(
    blocks,
    R.flatMap(block => block.actors),
    R.uniqueBy(actor => actor.id),
    R.sortBy(actor => actor.name.toLowerCase())
  );

const fieldRefsOf = (row: ActivityRow): FieldRef[] => {
  if (row.kind === 'field')
    return [{ schemaId: row.schemaId, label: row.label }];
  if (row.kind === 'table')
    return [
      { schemaId: row.schemaId, label: row.label },
      ...row.rows.flatMap(tableRow =>
        tableRow.cells.map(cell => ({
          schemaId: cell.schemaId,
          label: cell.label,
        }))
      ),
    ];
  return [];
};

export const toFieldOptions = (blocks: ActivityBlock[]): FieldRef[] =>
  R.pipe(
    blocks,
    R.flatMap(block => block.rows),
    R.flatMap(fieldRefsOf),
    R.uniqueBy(field => field.schemaId),
    R.sortBy(field => field.label.toLowerCase())
  );

const originOf = (block: ActivityBlock): ChangeOrigin =>
  isManualSource(block.source) ? 'manual' : 'automated';

export const matchesChangeOrigin = (
  origins: ChangeOrigin[] | undefined,
  block: ActivityBlock
) => !origins || origins.includes(originOf(block));

export const matchesActor = (
  actorIds: string[] | undefined,
  block: ActivityBlock
) => !actorIds || block.actors.some(actor => actorIds.includes(actor.id));

const eventOf = (row: ActivityRow): HistoryEvent =>
  row.kind === 'field' || row.kind === 'table' ? 'fields' : row.kind;

const matchesEvent = (events: HistoryEvent[] | undefined, row: ActivityRow) =>
  !events || events.includes(eventOf(row));

const filterCells = (
  keep: (schemaId: string) => boolean,
  row: ActivityRow
): ActivityRow | null => {
  if (row.kind === 'field') return keep(row.schemaId) ? row : null;
  if (row.kind !== 'table') return null;
  const rows = row.rows.flatMap(tableRow => {
    const cells = tableRow.cells.filter(cell => keep(cell.schemaId));
    return cells.length > 0 ? [{ ...tableRow, cells }] : [];
  });
  return rows.length > 0 ? { ...row, rows } : null;
};

const filterByField = (
  schemaIds: string[] | undefined,
  row: ActivityRow
): ActivityRow | null => {
  if (!schemaIds) return row;
  if (row.kind === 'table' && schemaIds.includes(row.schemaId)) return row;
  return filterCells(schemaId => schemaIds.includes(schemaId), row);
};

// Like the Field filter, an active visibility keeps field rows only. Per
// cell: `toHiddenFieldIds` already lists a hidden table's columns, and a
// visible table can still hold hidden columns.
const filterByVisibility = (
  isExcluded: ((schemaId: string) => boolean) | null,
  row: ActivityRow
): ActivityRow | null =>
  isExcluded ? filterCells(schemaId => !isExcluded(schemaId), row) : row;

export const toFieldExclusion = (
  visibility: FieldVisibility[] | undefined,
  hiddenIds: ReadonlySet<string> | undefined
): ((schemaId: string) => boolean) | null => {
  if (!hiddenIds || !visibility || visibility.length !== 1) return null;
  return visibility[0] === 'visible'
    ? schemaId => hiddenIds.has(schemaId)
    : schemaId => !hiddenIds.has(schemaId);
};

const filterRows = (
  block: ActivityBlock,
  toRow: (row: ActivityRow) => ActivityRow | null
): ActivityBlock[] => {
  const rows = block.rows.flatMap(row => {
    const next = toRow(row);
    return next ? [next] : [];
  });
  return rows.length > 0 ? [{ ...block, rows }] : [];
};

export const applyHistoryFilters = ({
  blocks,
  filters,
  hiddenIds,
}: {
  blocks: ActivityBlock[];
  filters: HistoryFilters;
  hiddenIds: ReadonlySet<string> | undefined;
}): ActivityBlock[] => {
  const isExcluded = toFieldExclusion(filters.visibility, hiddenIds);
  return blocks
    .filter(
      block =>
        matchesChangeOrigin(filters.changes, block) &&
        matchesActor(filters.changedBy, block)
    )
    .flatMap(block =>
      filterRows(block, row => {
        if (!matchesEvent(filters.event, row)) return null;
        const byField = filterByField(filters.field, row);
        return byField && filterByVisibility(isExcluded, byField);
      })
    );
};

// Status chips and unnamed actors render translated text the helpers cannot
// produce, so the caller supplies it.
export type SearchDisplay = {
  statusLabel: (status: string | null) => string | null;
  actorName: (source: ActivitySource) => string;
};

// Mirrors `QueueChangeRow`: a queue without a stored name shows as #<id>.
const getQueueText = (name: string | null, id: number | null) =>
  name ?? (id === null ? null : `#${id}`);

const textOf = (
  row: ActivityRow,
  display: SearchDisplay
): (string | null)[] => {
  switch (row.kind) {
    case 'field':
      return [row.label, row.previousValue, row.value];
    case 'table':
      return [
        row.label,
        ...row.rows.flatMap(tableRow =>
          tableRow.cells.flatMap(cell => textOf(cell, display))
        ),
      ];
    case 'status':
      return [
        display.statusLabel(row.previousValue),
        display.statusLabel(row.value),
      ];
    case 'queue':
      return [
        getQueueText(row.previousQueueName, row.previousQueueId),
        getQueueText(row.queueName, row.queueId),
      ];
    case 'automationBlocker':
      return row.fields.length > 0 ? row.fields : [row.blockerType];
    case 'opened':
      return [];
    default:
      return assertNever(row);
  }
};

const includes = (text: string | null, query: string) =>
  text !== null && text.toLowerCase().includes(query);

const searchRow = ({
  query,
  display,
  row,
}: {
  query: string;
  display: SearchDisplay;
  row: ActivityRow;
}): ActivityRow | null => {
  if (row.kind !== 'table')
    return textOf(row, display).some(text => includes(text, query))
      ? row
      : null;
  if (includes(row.label, query)) return row;
  const rows = row.rows.flatMap(tableRow => {
    const cells = tableRow.cells.filter(cell =>
      textOf(cell, display).some(text => includes(text, query))
    );
    return cells.length > 0 ? [{ ...tableRow, cells }] : [];
  });
  return rows.length > 0 ? { ...row, rows } : null;
};

export const applyHistorySearch = ({
  blocks,
  search,
  display,
}: {
  blocks: ActivityBlock[];
  search: string;
  display: SearchDisplay;
}): ActivityBlock[] => {
  const query = search.trim().toLowerCase();
  if (!query) return blocks;
  return blocks.flatMap(block =>
    includes(display.actorName(block.source), query)
      ? [block]
      : filterRows(block, row => searchRow({ query, display, row }))
  );
};
